import { flatten, flattenDeep, isPlainObject } from 'lodash-es';

interface ExtractErrorMessagesOptions {
  includeKeys?: boolean;
  errorKeys?: string[];
}

const extractErrorMessages = (
  error: any,
  options: ExtractErrorMessagesOptions = {}
): string[] | [string, string][] => {
  const { includeKeys = false, errorKeys = ['fields'] } = options;
  const errorBody = error?.body ?? error;

  const foundKey = errorKeys.find(key => errorBody?.error?.[key] != null);
  let errorFields: any = foundKey ? errorBody.error[foundKey] : null;

  if (errorFields == null) {
    // No structured field errors — show the human-readable message, never internal error metadata (http_code, subsystem, …).
    // Precedence matches ValidationError.message: error.message first, then top-level message.
    errorFields = errorBody?.error?.message ?? errorBody?.message ?? errorBody?.error ?? errorBody;
  }

  const collect = (v: any, key?: string): any[] => {
    if (v == null) return [];
    if (Array.isArray(v)) {
      const results = v.map((item, index) => {
        let newKey: string | undefined;
        if (includeKeys) {
          newKey = key ? `${key}[${index}]` : `[${index}]`;
        }
        return collect(item, newKey);
      });
      return includeKeys ? flatten(results) : flattenDeep(results);
    }
    if (typeof v === 'string') {
      const pythonErrorMatch = v.match(/\{\s*['"]errors['"]\s*:\s*\[([^\]]*)\]/);
      if (pythonErrorMatch) {
        const items: string[] = [...pythonErrorMatch[1].matchAll(/['"]([^'"]+)['"]/g)].map(m => m[1]);
        if (items.length) {
          return includeKeys && key ? items.map(item => [key, item]) : items;
        }
      }
      return includeKeys && key ? [[key, v]] : [v];
    }
    if (v instanceof Error && v.message) {
      return includeKeys && key ? [[key, v.message]] : [v.message];
    }
    if (isPlainObject(v)) {
      const getNewKey = (k: string) => {
        if (!includeKeys) return undefined;
        return key ? `${key}.${k}` : k;
      };
      const results = Object.entries(v).map(([k, val]) => collect(val, getNewKey(k)));
      return includeKeys ? flatten(results) : flattenDeep(results);
    }
    if (typeof v === 'number' || typeof v === 'boolean') {
      return includeKeys && key ? [[key, String(v)]] : [String(v)];
    }
    return [];
  };

  const collected = collect(errorFields);
  const messages = includeKeys ? collected : flattenDeep(collected);

  if (messages.length === 0 && error?.message) {
    if (includeKeys) {
      messages.push(['', error.message]);
    } else {
      messages.push(error.message);
    }
  }

  if (includeKeys) {
    const pairs = messages.filter((item): item is [string, string] => Array.isArray(item) && item.length === 2);
    const seen = new Set<string>();
    return pairs.filter((pair) => {
      const key = pair.join('|');
      if (!seen.has(key)) {
        seen.add(key);
        return true;
      }
      return false;
    });
  }

  return Array.from(new Set(messages.filter((item): item is string => typeof item === 'string' && item !== '')));
};

export default extractErrorMessages;
